/**
 * Cart Continuity — In-App Browser → default browser handoff
 *
 * Problem: a customer shopping inside an in-app browser (Instagram, Facebook,
 * TikTok, Gmail…) who gets pushed out to their real browser loses the session
 * cookie, and with it the cart. This module is both halves of the fix:
 *
 *   1. LINK BUILDING (runs in the in-app browser). Serialises the live cart
 *      into a URL that rebuilds it anywhere, then tries to hand that URL to the
 *      device's default browser. Exposed as `window.BL101CartContinuity` so the
 *      Rebuy Smart Cart's "Continue in Browser" custom code block can call it —
 *      Rebuy renders those blocks as Vue templates, which silently drop
 *      <script> tags, so all behaviour has to live here in the theme.
 *
 *   2. RESTORE (runs in the browser the link opens in). Reads the payload back
 *      and rebuilds the cart via the AJAX API, then opens the Smart Cart.
 *
 * Two link shapes are produced:
 *
 *   A. `/?cart_restore=<base64url payload>` — the default. Full fidelity: line
 *      item properties and selling plans survive, and the customer lands on the
 *      homepage with the Smart Cart open rather than on a bare /cart page.
 *
 *   B. `/cart/{variant}:{qty},…?storefront=true&…` — Shopify's native cart
 *      permalink, used only when shape A would exceed the URL length budget.
 *      Handled entirely server-side (no JS required), but Shopify drops line
 *      item properties and selling plans from permalinks, so it is the fallback
 *      rather than the default.
 */

(function CartContinuity() {
  'use strict';

  const CONFIG = {
    /** Query param carrying the encoded cart payload (shape A). */
    payloadParam: 'cart_restore',
    /** Query param that opens the Smart Cart without restoring anything. */
    restoredParam: 'cart_restored',
    /** Stamped as `ref` so these sessions are attributable in Shopify reports. */
    refValue: 'iab-continue',
    /** Older payloads are ignored — a week-old link rebuilding a cart is noise. */
    maxPayloadAgeMs: 7 * 24 * 60 * 60 * 1000,
    /** Past this total URL length, fall back to a native permalink (shape B). */
    maxUrlLength: 1800,
    /** How long to wait for Rebuy to boot before falling back to /cart. */
    rebuyReadyTimeoutMs: 8000,
    /** Remembers the last payload restored, so a refresh does not re-add. */
    guardKey: 'bl101:cart-continuity:restored',
    /** Set on <html> in an in-app browser; the Rebuy block's CSS gates on it. */
    iabClass: 'bl101-iab',
    /** A cached link older than this is rebuilt before use. */
    cacheTtlMs: 30000,
    /** `?cc_debug=1` narrates the restore to the console for the session. */
    debugParam: 'cc_debug',
    debugKey: 'bl101:cart-continuity:debug',
    /** A third-party asset stalling `load` should not hold the cart shut. */
    documentLoadTimeoutMs: 4000,
  };

  // ─── Diagnostics ───────────────────────────────────────────

  // Land on any page with ?cc_debug=1 to turn narration on for the rest of the
  // session — it has to outlive the landing itself, since a restore can send the
  // customer on to /cart before anything useful has been read.
  const debugEnabled = (function readDebugFlag() {
    try {
      if (new URLSearchParams(window.location.search).get(CONFIG.debugParam)) {
        window.sessionStorage.setItem(CONFIG.debugKey, '1');
        return true;
      }
      return window.sessionStorage.getItem(CONFIG.debugKey) === '1';
    } catch (error) {
      return false;
    }
  })();

  function debug(...args) {
    if (debugEnabled) console.log('[cart-continuity]', ...args);
  }

  // ─── Environment detection ─────────────────────────────────

  const IAB_PATTERNS = [
    /FBAN|FBAV|FB_IAB|FBIOS/i, // Facebook, Messenger
    /Instagram/i,
    /TikTok|BytedanceWebview|musical_ly/i,
    /Snapchat/i,
    /Pinterest/i,
    /\bTwitter/i,
    /LinkedInApp/i,
    /MicroMessenger/i, // WeChat
    /Line\//i,
    /KAKAOTALK/i,
    /\bReddit/i,
    /GSA\//i, // Google app on iOS
    /SamsungBrowser\/[^\s]*CrossApp/i,
    /\bwv\b/i, // Android WebView
  ];

  function isIOS() {
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    // iPadOS in desktop mode reports as a Mac; touch points give it away.
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  }

  function isInAppBrowser() {
    const ua = navigator.userAgent || navigator.vendor || '';
    if (IAB_PATTERNS.some((pattern) => pattern.test(ua))) return true;

    // Every real iOS browser ships a `Safari` token; iOS webviews do not. Chrome,
    // Firefox, Edge and Opera identify themselves explicitly, and a home-screen
    // PWA also drops the token legitimately — rule all of those out before
    // treating a missing token as evidence of an in-app browser.
    if (isIOS() && !/Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) {
      return navigator.standalone !== true;
    }
    return false;
  }

  // ─── Payload encoding ──────────────────────────────────────

  // Base64url, so the payload survives a URL without percent-escaping. Goes
  // through TextEncoder because btoa() throws on non-Latin-1 (a cart note or a
  // gift message can easily contain an emoji or an accent).
  function encodePayload(object) {
    const bytes = new TextEncoder().encode(JSON.stringify(object));
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function decodePayload(raw) {
    try {
      const base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
      const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      const payload = JSON.parse(new TextDecoder().decode(bytes));
      if (!payload || payload.v !== 1 || !Array.isArray(payload.i)) return null;
      if (payload.t && Date.now() - payload.t > CONFIG.maxPayloadAgeMs) return null;
      return payload;
    } catch (error) {
      return null;
    }
  }

  // Cheap 32-bit hash — only ever compared against itself, so collision
  // resistance does not matter; keeping the guard value short does.
  function fingerprint(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
      hash = (hash * 31 + string.charCodeAt(i)) | 0;
    }
    return String(hash);
  }

  // ─── Cart reads ────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  function sellingPlanId(item) {
    const id = item?.selling_plan_allocation?.selling_plan?.id;
    return id ? String(id) : '';
  }

  // Only manually entered codes can be replayed. Automatic discounts and
  // Shopify Scripts also land in cart_level_discount_applications, but they
  // re-apply on their own in the new browser and have no code to pass along.
  function discountCodes(cart) {
    if (Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0) {
      return cart.discount_codes
        .filter((entry) => entry.applicable !== false && entry.code)
        .map((entry) => entry.code);
    }
    return (cart.cart_level_discount_applications || [])
      .filter((application) => application.type === 'discount_code' && application.title)
      .map((application) => application.title);
  }

  // ─── Link building ─────────────────────────────────────────

  // Cart attributes owned by apps that set them per session. Rebuy's Smart Cart
  // stamps _attribution/_source/_barId and Redo adds its own flags; replaying a
  // stale bar id into a fresh browser misattributes that session and bloats the
  // URL for nothing, since both apps re-stamp on arrival. Everything else —
  // notably the theme's own _free_gift_removed — travels.
  const APP_OWNED_ATTRIBUTES = [/^_attribution$/, /^_source$/, /^_barId$/, /^redo_/i];

  function portableAttributes(attributes) {
    const portable = {};
    Object.entries(attributes || {}).forEach(([key, value]) => {
      if (!value) return;
      if (APP_OWNED_ATTRIBUTES.some((pattern) => pattern.test(key))) return;
      portable[key] = value;
    });
    return portable;
  }

  function buildPayloadUrl(cart) {
    const payload = {
      v: 1,
      t: Date.now(),
      i: cart.items.map((item) => {
        const line = { id: String(item.variant_id), q: item.quantity };
        if (item.properties && Object.keys(item.properties).length > 0) line.p = item.properties;
        const plan = sellingPlanId(item);
        if (plan) line.s = plan;
        return line;
      }),
    };

    const codes = discountCodes(cart);
    if (codes.length > 0) payload.d = codes;
    if (cart.note) payload.n = cart.note;
    const attributes = portableAttributes(cart.attributes);
    if (Object.keys(attributes).length > 0) payload.a = attributes;

    const params = new URLSearchParams();
    params.set(CONFIG.payloadParam, encodePayload(payload));
    params.set('ref', CONFIG.refValue);
    return window.location.origin + '/?' + params.toString();
  }

  function buildPermalinkUrl(cart) {
    const items = cart.items.map((item) => item.variant_id + ':' + item.quantity).join(',');

    const params = new URLSearchParams();
    // Without this the permalink jumps straight to checkout, skipping the cart.
    params.set('storefront', 'true');
    params.set('ref', CONFIG.refValue);

    const codes = discountCodes(cart);
    if (codes.length > 0) params.set('discount', codes.join(','));
    if (cart.note) params.set('note', cart.note);
    Object.entries(portableAttributes(cart.attributes)).forEach(([key, value]) => {
      params.set('attributes[' + key + ']', value);
    });

    return window.location.origin + '/cart/' + items + '?' + params.toString();
  }

  async function buildContinueUrl() {
    const cart = await fetchCart();
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return null;

    const url = buildPayloadUrl(cart);
    // A very large cart pushes the payload past what older browsers and proxies
    // will carry. Shed the properties and selling plans rather than the items.
    if (url.length > CONFIG.maxUrlLength) return buildPermalinkUrl(cart);
    return url;
  }

  // The click handler has to be able to write to the clipboard, and Safari only
  // allows that inside an untouched user gesture — awaiting /cart.js first
  // forfeits it. So keep a link warm while the cart is open and read it
  // synchronously on click.
  let cachedUrl = null;
  let cachedAt = 0;

  function refreshCachedUrl() {
    return buildContinueUrl().then((url) => {
      cachedUrl = url;
      cachedAt = Date.now();
      return url;
    });
  }

  function cachedUrlIfFresh() {
    if (!cachedUrl) return null;
    return Date.now() - cachedAt < CONFIG.cacheTtlMs ? cachedUrl : null;
  }

  // ─── Handing off to the default browser ────────────────────

  // Each in-app browser honours a different escape hatch, and none of them
  // report success or failure — hence the visible copy-link fallback.
  function escapeUrlFor(url) {
    const ua = navigator.userAgent || '';
    // Android's intent:// syntax terminates the URL at the first '#', so a
    // fragment would swallow the Intent block. Nothing built here carries one,
    // but drop it rather than depend on that.
    const bare = url.replace(/^https?:\/\//, '').split('#')[0];

    if (/Android/i.test(ua)) {
      return 'intent://' + bare + '#Intent;scheme=https;action=android.intent.action.VIEW;end';
    }
    if (isIOS()) {
      return 'x-safari-https://' + bare;
    }
    return null;
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(() => copyViaTextarea(text));
    }
    return Promise.resolve(copyViaTextarea(text));
  }

  function copyViaTextarea(text) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      textarea.remove();
      return copied;
    } catch (error) {
      return false;
    }
  }

  function rootFor(element) {
    return element?.closest?.('[data-cart-continuity-root]') || null;
  }

  function setState(root, state) {
    if (root) root.setAttribute('data-state', state);
  }

  function showFallback(root, url) {
    if (!root) return;
    setState(root, 'fallback');
    const field = root.querySelector('[data-cart-continuity-url]');
    if (field) {
      field.value = url;
      field.setAttribute('value', url);
    }
  }

  /**
   * Entry point for the Rebuy custom code block's button.
   * @param {HTMLElement} trigger - the clicked element.
   */
  function continueInBrowser(trigger) {
    const root = rootFor(trigger);
    const ready = cachedUrlIfFresh();

    if (ready) {
      handoff(ready, root);
      return Promise.resolve(ready);
    }

    // Cold path: the gesture is already spent by the time the cart comes back,
    // so skip the clipboard write and go straight to the escape URL.
    setState(root, 'working');
    return refreshCachedUrl().then((url) => {
      if (!url) {
        setState(root, 'empty');
        return null;
      }
      handoff(url, root);
      return url;
    });
  }

  function handoff(url, root) {
    copyToClipboard(url);
    setState(root, 'working');

    const escapeUrl = escapeUrlFor(url);

    if (!escapeUrl) {
      // No known escape for this platform. window.open at least reports back,
      // so a returned window means the handoff genuinely happened.
      const opened = window.open(url, '_blank', 'noopener');
      if (opened) return;
      showFallback(root, url);
      return;
    }

    // A blocked custom scheme is a silent no-op in most webviews, though Android
    // occasionally throws on an unhandled intent:// — either way, fall through
    // to the fallback rather than leaving the button spinning.
    try {
      window.location.href = escapeUrl;
    } catch (error) {
      showFallback(root, url);
      return;
    }

    // Custom schemes report neither success nor failure. Still being visible a
    // beat later means it did not take, so surface the link for pasting — or for
    // the app's own "open in browser" menu.
    window.setTimeout(() => {
      if (document.visibilityState === 'visible') showFallback(root, url);
    }, 1400);
  }

  // ─── Rebuy Smart Cart ──────────────────────────────────────

  function waitForSmartCart() {
    const smartCart = window.Rebuy?.SmartCart;
    if (smartCart && smartCart.status === 'ready') return Promise.resolve(smartCart);

    return new Promise((resolve) => {
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        const candidate = window.Rebuy?.SmartCart;
        if (candidate && candidate.status === 'ready') {
          window.clearInterval(timer);
          resolve(candidate);
        } else if (Date.now() - startedAt > CONFIG.rebuyReadyTimeoutMs) {
          window.clearInterval(timer);
          resolve(null);
        }
      }, 200);
    });
  }

  // Rebuy reports `ready` as soon as its Vue app mounts, which on a cold mobile
  // load is well before its own stylesheet and web fonts have landed. Opening the
  // drawer at that moment is what makes it flash in half-styled — on desktop the
  // gap is small enough to miss. Wait for the document to finish instead.
  function whenDocumentLoaded() {
    if (document.readyState === 'complete') return Promise.resolve();
    return new Promise((resolve) => {
      window.addEventListener('load', resolve, { once: true });
      window.setTimeout(resolve, CONFIG.documentLoadTimeoutMs);
    });
  }

  // Two frames: one for Rebuy to render the cart just pushed into it, one for
  // that render to paint before the drawer animates in over the top of it.
  function afterNextPaint() {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
    });
  }

  // Rebuy keeps its own copy of the cart and does not watch the AJAX API, so a
  // restore performed with /cart/add.js has to be pushed into it by hand.
  async function openCart() {
    const smartCart = await waitForSmartCart();

    if (smartCart) {
      const cart = await fetchCart();
      if (cart && typeof smartCart.setCart === 'function') {
        try {
          smartCart.setCart(cart);
        } catch (error) {
          /* a stale count is survivable; opening the cart is not optional */
        }
      }
      document.dispatchEvent(new CustomEvent('rebuy:cart.change', { detail: { cart } }));
      if (typeof smartCart.show === 'function') {
        await whenDocumentLoaded();
        await afterNextPaint();
        debug('openCart: showing Smart Cart');
        smartCart.show();
        return;
      }
    }

    // Rebuy never came up. The cart page is the honest destination.
    debug('openCart: Smart Cart unavailable, falling back to /cart');
    window.location.assign('/cart');
  }

  // ─── Restore ───────────────────────────────────────────────

  function propertiesKey(properties) {
    if (!properties) return '';
    return Object.keys(properties)
      .sort()
      .map((key) => key + '=' + properties[key])
      .join('&');
  }

  function linesMatch(item, line) {
    return (
      String(item.variant_id) === String(line.id) &&
      propertiesKey(item.properties) === propertiesKey(line.p) &&
      sellingPlanId(item) === (line.s || '')
    );
  }

  function postJson(path, body) {
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // One unavailable variant makes /cart/add.js reject the whole batch, which
  // would lose a sold-out item *and* everything alongside it. Retry the items
  // individually so the rest of the cart still arrives.
  async function addItems(items) {
    if (items.length === 0) return;

    const batch = await postJson('/cart/add.js', { items }).catch(() => null);
    if (batch && batch.ok) return;
    debug('add: batch rejected', batch ? batch.status : 'network error');

    for (const item of items) {
      // Sequential on purpose: concurrent /cart/add.js calls race on the cart
      // and Shopify can drop one of them.
      // eslint-disable-next-line no-await-in-loop
      const response = await postJson('/cart/add.js', { items: [item] }).catch(() => null);
      if (!response || !response.ok) {
        debug('add: line rejected', item.id, response ? response.status : 'network error');
      }
    }
  }

  // Discount codes can only be re-applied by hitting /discount/<code>, which sets
  // the discount cookie. A same-origin fetch picks that cookie up just as a real
  // visit would, and unlike a navigation a failure costs nothing — the cart is
  // already rebuilt, so a dropped code is a smaller loss than a dead end on a
  // 404. Codes are replayed in reverse so that if Shopify replaces rather than
  // stacks them, the cart's primary code is the one left standing.
  async function applyDiscounts(codes) {
    for (const code of [...codes].reverse()) {
      // eslint-disable-next-line no-await-in-loop
      await fetch('/discount/' + encodeURIComponent(code), {
        credentials: 'same-origin',
      }).catch(() => null);
    }
  }

  // Top up to the target quantity rather than blindly adding: a customer who
  // opens the link twice, or who already had the item in this browser, should
  // end up with the cart they left, not a doubled one.
  function missingAdditions(cart, payload) {
    const additions = [];
    payload.i.forEach((line) => {
      const target = Math.max(1, Number(line.q) || 1);
      const existing = (cart.items || [])
        .filter((item) => linesMatch(item, line))
        .reduce((total, item) => total + item.quantity, 0);
      const missing = target - existing;
      if (missing <= 0) return;

      const addition = { id: Number(line.id), quantity: missing };
      if (line.p) addition.properties = line.p;
      if (line.s) addition.selling_plan = Number(line.s);
      additions.push(addition);
    });
    return additions;
  }

  /** Did any line from the payload actually make it into this cart? */
  function cartSatisfies(cart, payload) {
    return payload.i.some((line) => (cart.items || []).some((item) => linesMatch(item, line)));
  }

  /** @returns {Promise<boolean>} whether the cart ended up holding any of it. */
  async function restore(payload) {
    let cart = (await fetchCart()) || { items: [] };
    let additions = missingAdditions(cart, payload);
    debug('restore: payload has', payload.i.length, 'line(s),', additions.length, 'missing');

    if (additions.length > 0) {
      await addItems(additions);

      // Re-read rather than trust the response. /cart/add.js can 200 on a partial
      // add, and a request that never arrived is silent by design — on a cold
      // mobile load these fetches are also the first thing iOS suspends if the
      // customer flicks away, which is exactly how a restore ends up empty.
      cart = (await fetchCart()) || { items: [] };
      additions = missingAdditions(cart, payload);
      if (additions.length > 0) {
        debug('restore: retrying', additions.length, 'line(s) still missing');
        await addItems(additions);
        cart = (await fetchCart()) || { items: [] };
      }
    }

    const update = {};
    // Never overwrite a note the customer typed in this browser.
    if (payload.n && !cart.note) update.note = payload.n;
    if (payload.a && Object.keys(payload.a).length > 0) update.attributes = payload.a;
    if (Object.keys(update).length > 0) await postJson('/cart/update.js', update).catch(() => null);

    if (Array.isArray(payload.d) && payload.d.length > 0) await applyDiscounts(payload.d);

    const restored = cartSatisfies(cart, payload);
    debug('restore: finished with', (cart.items || []).length, 'item(s); restored =', restored);

    // Every line failed. An empty drawer sliding open only advertises that the
    // handoff broke — leave the customer on the page they landed on instead.
    if (restored) await openCart();
    return restored;
  }

  function stripParams(keys) {
    try {
      const url = new URL(window.location.href);
      let changed = false;
      keys.forEach((key) => {
        if (url.searchParams.has(key)) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
      if (changed) {
        window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
      }
    } catch (error) {
      /* a cosmetic URL is not worth failing the restore over */
    }
  }

  async function handleLanding() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(CONFIG.payloadParam);

    // `?cart_restored=1` on its own just opens the cart. Nothing in this file
    // generates it — it exists so any other link (an email, an SMS) can land a
    // customer with the Smart Cart already open.
    if (!raw) {
      if (params.get(CONFIG.restoredParam)) {
        stripParams([CONFIG.restoredParam]);
        openCart();
      }
      return;
    }

    const payload = decodePayload(raw);
    if (!payload) {
      debug('landing: payload malformed or expired');
      stripParams([CONFIG.payloadParam]);
      return;
    }

    const guard = fingerprint(raw);
    let alreadyRestored = false;
    try {
      alreadyRestored = window.localStorage.getItem(CONFIG.guardKey) === guard;
    } catch (error) {
      /* private mode — fall back to restoring, which is idempotent by quantity */
    }

    if (alreadyRestored) {
      // The guard only ever proves the restore *ran here before*, so check the
      // cart before believing it. The iOS handoff opens this same link in Safari
      // on its own, and if that visit was backgrounded before its adds landed,
      // trusting the guard leaves the customer staring at the cart they came for
      // minus the products.
      const cart = (await fetchCart()) || { items: [] };
      if (cartSatisfies(cart, payload)) {
        debug('landing: already restored and cart matches — just opening it');
        stripParams([CONFIG.payloadParam]);
        openCart();
        return;
      }
      debug('landing: guard is set but the cart is missing the items — restoring again');
    }

    const restored = await restore(payload);

    // Only strip once the work has landed. Clearing the payload up front made a
    // refresh the one thing that could not recover a half-finished restore.
    // `ref` is deliberately left in place for analytics to pick up.
    stripParams([CONFIG.payloadParam]);

    if (restored) {
      try {
        window.localStorage.setItem(CONFIG.guardKey, guard);
      } catch (error) {
        /* nothing to do; the quantity top-up still guards against doubling */
      }
    }
  }

  // ─── Boot ──────────────────────────────────────────────────

  function markEnvironment() {
    if (isInAppBrowser()) document.documentElement.classList.add(CONFIG.iabClass);
  }

  function watchCart() {
    if (!isInAppBrowser()) return;

    let timer = null;
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        refreshCachedUrl();
      }, 250);
    };

    ['rebuy:smartcart.ready', 'rebuy:smartcart.show', 'rebuy:cart.change'].forEach((name) => {
      document.addEventListener(name, schedule);
    });
  }

  window.BL101CartContinuity = {
    isInAppBrowser,
    buildContinueUrl,
    continueInBrowser,
    openCart,
    copyToClipboard,
    /** Wired to the copy button in the Rebuy block's fallback state. */
    copyLink(trigger) {
      const root = rootFor(trigger);
      const url = cachedUrlIfFresh();
      if (url) {
        copyToClipboard(url);
        setState(root, 'copied');
        return Promise.resolve(url);
      }
      return refreshCachedUrl().then((fresh) => {
        if (!fresh) return null;
        copyToClipboard(fresh);
        showFallback(root, fresh);
        setState(root, 'copied');
        return fresh;
      });
    },
  };

  markEnvironment();
  watchCart();
  handleLanding().catch((error) => debug('landing: failed', error));
})();
